import { useDisclosure } from '@chakra-ui/hooks'
import {
  Tag,
  TagCloseButton,
  TagLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@chakra-ui/react'
import type { RefObject } from 'react'
import React, { useMemo, useRef, useEffect } from 'react'
import { colors } from '../../../../ui-kit'

import type {
  CustomMetadataResponse,
  FormValidationObject,
  RemoveFilterProps,
  SearchQueryJson,
} from '../../../interfaces'
import FilterText from '../FilterText'
import { getInputForm } from '../InputForms'

interface Props {
  searchQueryJson: SearchQueryJson
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  customMetadataField: CustomMetadataResponse
  validationObject: FormValidationObject
  groupId: number
  filterId: number
  maxWidthRef: RefObject<any>
  setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  removeFilter(removeFilterProps: RemoveFilterProps): void
}

const Filter = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataFieldsMapping,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    maxWidthRef,
    setSearchQueryJson,
    removeFilter,
  } = props

  const {
    isOpen: isOpenFilterForm,
    onClose: onCloseFilterForm,
    onOpen: onOpenFilterForm,
  } = useDisclosure({
    defaultIsOpen: true,
  })

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId].expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const InputForm = useMemo(
    () =>
      getInputForm({ fieldName: expression.fieldName, customMetadataField }),
    [expression, customMetadataField],
  )

  const maxFilterWidth = useMemo<number | undefined>(
    () => maxWidthRef.current?.offsetWidth,
    [maxWidthRef],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const initialFocusRef = useRef<HTMLElement>(null)

  return (
    <Popover
      placement="bottom-start"
      initialFocusRef={initialFocusRef}
      isOpen={isOpenFilterForm}
      onClose={onCloseFilterForm}
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <Tag
          cursor="pointer"
          maxWidth={maxFilterWidth}
          marginTop="0.5em"
          paddingY="0.5em"
          paddingX="1em"
          height="2rem"
          borderRightRadius="0"
          borderLeftRadius={filterId === 0 ? undefined : '0'}
          _hover={error === null ? { bgColor: colors.appBlueyGrey } : {}}
          bgColor={error !== null ? '#fed9db' : undefined}
          onClick={() => {
            onOpenFilterForm()
          }}
        >
          <TagLabel color={colors.appSlate}>
            <FilterText
              {...{
                searchQueryJson,
                customMetadataFieldsMapping,
                validationObject,
                textColor: error !== null ? '#fd3a57' : colors.appSlate,
                groupId,
                filterId,
              }}
            />
          </TagLabel>
          <TagCloseButton
            onClick={() =>
              void removeFilter({
                json: searchQueryJson,
                groupId,
                filterId,
              })
            }
          />
        </Tag>
      </PopoverTrigger>
      <PopoverContent
        _highlighted={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
        }}
        _focus={{ border: 'solid 0.5px #cdd5d', borderRadius: '0px' }}
        _focusVisible={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
          outline: 'none',
        }}
        _focusWithin={{
          border: 'solid 0.5px #cdd5d',
          borderRadius: '0px',
        }}
        _active={{ border: 'solid 0.5px #cdd5d', borderRadius: '0px' }}
        borderWidth="0.5px"
        borderColor="#cdd5df"
        borderRadius="0"
        padding="0.75em"
      >
        <InputForm
          searchQueryJson={searchQueryJson}
          validationObject={validationObject}
          customMetadataField={customMetadataField}
          groupId={groupId}
          filterId={filterId}
          initialFocusRef={initialFocusRef}
          setSearchQueryJson={setSearchQueryJson}
        />
      </PopoverContent>
    </Popover>
  )
}

export default Filter
